/* eslint-disable no-console */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { StripeElement, StripeElements } from '@stripe/stripe-js';
import {
  CalendarWithExclaimationIcon,
  EyeCloseIcon,
  EyeIcon,
  LockWithDotIcon,
  ProhibitedIcon,
  ScanpayLogoIcon
} from 'assets/icons';
import { selectWebViewParams, selectWebViewTeamId } from 'containers/auth/authSlice';
import Loader from 'components/loader/Loader';
import StripeService from 'services/stripe';
import { useCreateEphemeralKeyMutation, useLazyGetCardByIdQuery } from 'containers/treasury/api';
import { ShowToastParams, WebviewFunctions } from 'constants/common';
import { logAnalyticEvent } from 'utils/analytics';
import CardBackground from './CardBackground';
import CardDetails from './CardDetails';
import useCardSizing from '../hooks/useCardSizing';
import { TreasuryCTEvents } from '../events';
import { CardData } from '../treasury.model';
import { WEBVIEW_NOTIFIER_TYPES } from 'constants/notifierConstants';
import { ETREASURY_CARD_STATUS, ETREASURY_CARD_TYPES } from '../constants';

interface CardViewProps {
  className?: string;
}

const CardView: React.FC<CardViewProps> = ({ className = '' }) => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const webViewTeamId = useAppSelector(selectWebViewTeamId);
  const {
    cardId: spendAccountCardId,
    spendAccountId,
    connectAccountId,
    scaleHeight,
    scaleWidth
  } = useAppSelector(selectWebViewParams);

  const stripePromise = useMemo(() => StripeService.getInstance(connectAccountId), [connectAccountId]);

  const [isCardLoaded, setIsCardLoaded] = useState(false);
  const [showDetails, setShowDetails] = useState(true);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);

  const stripeElementsRef = useRef<StripeElements | null>(null);
  const mountedElementsRef = useRef<Set<StripeElement>>(new Set());
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  const [createEphemeralKey] = useCreateEphemeralKeyMutation();
  const [getCardById, { data: cardByIdData, isFetching: isFetchingCardDetails }] = useLazyGetCardByIdQuery();
  const { Data: cardDataFromApi } = cardByIdData || {};

  // Memoize expensive computations
  const isPhysicalCard = useMemo(
    () => cardDataFromApi?.cardType.toLowerCase() === ETREASURY_CARD_TYPES.PHYSICAL,
    [cardDataFromApi?.cardType]
  );

  const cardNumberDotColor = useMemo(() => {
    if (isPhysicalCard) return 'bg-white';
    if (cardDataFromApi?.status !== ETREASURY_CARD_STATUS.ACTIVE) return 'bg-primaryText/60';
    return 'bg-primaryText';
  }, [isPhysicalCard, cardDataFromApi?.status]);

  const contentTextColor = useMemo(() => {
    if (!isPhysicalCard) {
      if (cardDataFromApi?.status !== ETREASURY_CARD_STATUS.ACTIVE) {
        return 'text-primaryText text-opacity-60';
      }
      return 'text-primaryText';
    }
    return 'text-primary';
  }, [isPhysicalCard, cardDataFromApi?.status]);

  // Use custom hook for element sizing
  const { getElementHeight, getElementWidth } = useCardSizing({ scaleHeight, scaleWidth });

  const handleLoadCardDetails = useCallback(
    async (cardData: CardData) => {
      try {
        const stripe = await stripePromise;

        if (!stripe) {
          return;
        }

        const nonceResult = await stripe.createEphemeralKeyNonce({
          issuingCard: cardData?.cardProviderId
        });

        if (!nonceResult.nonce) {
          return;
        }

        const teamId = webViewTeamId || currentTeam?.id?.toString();
        if (!teamId) {
          return;
        }

        const result = await createEphemeralKey({
          spendAccountId: spendAccountId,
          spendAccountCardId: spendAccountCardId,
          nonce: nonceResult.nonce,
          teamId: teamId,
          cardId: cardData?.cardProviderId
        }).unwrap();

        if (!result?.Data?.ephemeralKeySecret) {
          return;
        }

        const elements = stripe.elements({
          fonts: [
            {
              cssSrc: 'https://fonts.googleapis.com/css?family=Lato:400,500,600,700&display=swap'
            }
          ]
        });

        stripeElementsRef.current = elements;

        let textColor = '#4F4F4F';
        if (cardData.cardType?.toLowerCase() === ETREASURY_CARD_TYPES.PHYSICAL) {
          textColor = '#FFFFFF';
        } else if (
          cardData.cardType?.toLowerCase() === ETREASURY_CARD_TYPES.VIRTUAL &&
          cardData.status !== ETREASURY_CARD_STATUS.ACTIVE
        ) {
          textColor = '#4F4F4F99';
        } else {
          textColor = '#4F4F4F';
        }

        const cardNumberStyle = {
          base: {
            fontFamily: '"Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            fontSize: '20px',
            color: textColor,
            fontWeight: '700'
          }
        };

        const cardCvcExpiryStyle = {
          base: {
            fontFamily: '"Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            fontSize: '15px',
            color: textColor,
            fontWeight: '700'
          }
        };

        const elementConfig = {
          nonce: nonceResult.nonce,
          issuingCard: cardData?.cardProviderId,
          ephemeralKeySecret: result.Data.ephemeralKeySecret
        };

        // Mount card number element
        const elementCardNumber = elements.create('issuingCardNumberDisplay', {
          ...elementConfig,
          style: cardNumberStyle
        });
        elementCardNumber.mount('#card-number-display');
        mountedElementsRef.current.add(elementCardNumber);

        elementCardNumber.on('ready', () => {
          const timeout = setTimeout(() => {
            setIsDetailsLoading(false);
          }, 1500);
          return () => clearTimeout(timeout);
        });

        // Mount CVC element
        const elementCvc = elements.create('issuingCardCvcDisplay', { ...elementConfig, style: cardCvcExpiryStyle });
        elementCvc.mount('#card-cvc-display');
        mountedElementsRef.current.add(elementCvc);

        // Mount expiry element
        const elementExpiry = elements.create('issuingCardExpiryDisplay', {
          ...elementConfig,
          style: cardCvcExpiryStyle
        });
        elementExpiry.mount('#card-expiry-display');
        mountedElementsRef.current.add(elementExpiry);

        // Mount copy button
        const cardNumberCopy = elements.create('issuingCardCopyButton', {
          toCopy: 'number'
        });
        cardNumberCopy.mount('#card-number-copy');
        mountedElementsRef.current.add(cardNumberCopy);

        const cardExpiryCopy = elements.create('issuingCardCopyButton', {
          toCopy: 'expiry'
        });
        cardExpiryCopy.mount('#card-expiry-copy');
        mountedElementsRef.current.add(cardExpiryCopy);

        const cardCvcCopy = elements.create('issuingCardCopyButton', {
          toCopy: 'cvc'
        });
        cardCvcCopy.mount('#card-cvc-copy');
        mountedElementsRef.current.add(cardCvcCopy);

        setIsCardLoaded(true);
        if (window?.flutter_inappwebview?.callHandler) {
          window.flutter_inappwebview.callHandler(WebviewFunctions.WEBVIEW_LOADED);
        }
        cardNumberCopy.on('click', () => {
          if (window?.flutter_inappwebview?.callHandler) {
            const toastParams: ShowToastParams = {
              message: 'Card number copied',
              type: WEBVIEW_NOTIFIER_TYPES.SUCCESS
            };
            window.flutter_inappwebview.callHandler(WebviewFunctions.SHOW_TOAST, toastParams);
          }
        });
        cardExpiryCopy.on('click', () => {
          if (window?.flutter_inappwebview?.callHandler) {
            const toastParams: ShowToastParams = {
              message: 'Expiry copied',
              type: WEBVIEW_NOTIFIER_TYPES.SUCCESS
            };
            window.flutter_inappwebview.callHandler(WebviewFunctions.SHOW_TOAST, toastParams);
          }
        });
        cardCvcCopy.on('click', () => {
          if (window?.flutter_inappwebview?.callHandler) {
            const toastParams: ShowToastParams = {
              message: 'CVV copied',
              type: WEBVIEW_NOTIFIER_TYPES.SUCCESS
            };
            window.flutter_inappwebview.callHandler(WebviewFunctions.SHOW_TOAST, toastParams);
          }
        });
      } catch (error) {
        logAnalyticEvent(TreasuryCTEvents.CARD_VIEW_ERROR, {
          error: error instanceof Error ? error.message : 'Unknown error'
        });
        setIsDetailsLoading(false);
      }
    },
    [stripePromise, webViewTeamId, currentTeam?.id, spendAccountId, spendAccountCardId, createEphemeralKey]
  );

  const initialiseCardDetails = useCallback(async () => {
    if (spendAccountCardId && spendAccountId) {
      const cardData = await getCardById({
        teamId: webViewTeamId || currentTeam?.id,
        spendAccountId,
        cardId: spendAccountCardId
      }).unwrap();
      if (cardData.Data) {
        setIsDetailsLoading(true);
        await handleLoadCardDetails(cardData.Data);
      }
    }
  }, [spendAccountCardId, spendAccountId, webViewTeamId, currentTeam?.id, getCardById, handleLoadCardDetails]);

  useEffect(() => {
    initialiseCardDetails();
  }, [initialiseCardDetails]);

  // Cleanup function for Stripe elements
  const cleanupStripeElements = useCallback(() => {
    mountedElementsRef.current.forEach(el => {
      try {
        el.destroy();
      } catch (e) {
        console.warn('Failed to destroy Stripe element:', e);
      }
    });
    mountedElementsRef.current.clear();
    stripeElementsRef.current = null;

    timeoutsRef.current.forEach(id => clearTimeout(id));
    timeoutsRef.current = [];
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupStripeElements();
    };
  }, [cleanupStripeElements]);

  const handleViewDetails = useCallback(() => {
    if (!isCardLoaded) {
      setIsDetailsLoading(true);
      handleLoadCardDetails(cardDataFromApi);
    }
    setShowDetails(prev => !prev);
  }, [isCardLoaded, handleLoadCardDetails, cardDataFromApi]);

  const getStatusBannerConfig = (status: string) => {
    switch (status.toLowerCase()) {
      case ETREASURY_CARD_STATUS.CLOSED:
        return {
          text: 'Your card is closed',
          bgColor: 'bg-error',
          iconType: ETREASURY_CARD_STATUS.CLOSED
        };
      case ETREASURY_CARD_STATUS.INACTIVE:
        return {
          text: 'Your card is frozen',
          bgColor: isPhysicalCard ? 'bg-[#00000080]' : 'bg-primary',
          iconType: ETREASURY_CARD_STATUS.INACTIVE
        };
      case ETREASURY_CARD_STATUS.EXPIRED:
        return {
          text: 'Your card is expired',
          bgColor: 'bg-error',
          iconType: ETREASURY_CARD_STATUS.EXPIRED
        };
      default:
        return {
          text: '',
          bgColor: '',
          iconType: ''
        };
    }
  };

  const getStatusIcon = ({ type }: { type: string }) => {
    const iconClass = `h-4 w-4 ${
      !isPhysicalCard && type === ETREASURY_CARD_STATUS.INACTIVE ? 'text-primaryText path-fill-current' : 'text-white'
    }`;

    switch (type.toLowerCase()) {
      case ETREASURY_CARD_STATUS.CLOSED:
        return <ProhibitedIcon className={iconClass} />;
      case ETREASURY_CARD_STATUS.INACTIVE:
        return <LockWithDotIcon className={iconClass} />;
      case ETREASURY_CARD_STATUS.EXPIRED:
        return <CalendarWithExclaimationIcon className={iconClass} />;
      default:
        return null;
    }
  };

  const renderStatusBanner = ({ config }: { config: { text: string; bgColor: string; iconType: string } }) => (
    <div className={`${config.bgColor} flex items-center gap-1 rounded p-1 text-white`}>
      {getStatusIcon({ type: config.iconType })}
      <span
        className={`text-sbase font-semibold ${
          !isPhysicalCard && config.iconType === ETREASURY_CARD_STATUS.INACTIVE ? 'text-primaryText' : 'text-white'
        }`}>
        {config.text}
      </span>
    </div>
  );

  const statusBannerConfig = useMemo(
    () => getStatusBannerConfig(cardDataFromApi?.status || ''),
    [cardDataFromApi?.status, isPhysicalCard]
  );

  if (isFetchingCardDetails || !cardDataFromApi) {
    return <Loader />;
  }

  return (
    <div className={`w-full ${className}`} role="region" aria-label="Virtual card display">
      <CardBackground
        cardType={cardDataFromApi.cardType}
        cardStatus={cardDataFromApi.status}
        isPhysicalCard={isPhysicalCard}
        getElementHeight={getElementHeight}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <ScanpayLogoIcon
              className={`path-fill-current ${contentTextColor}`}
              style={{ height: getElementHeight(16), width: getElementWidth(16) }}
              aria-hidden="true"
            />
            <h2 className={`text-px13 font-medium tracking-wider ${contentTextColor}`}>
              {cardDataFromApi.cardType.toUpperCase()} CARD
            </h2>
          </div>
          <div
            onClick={handleViewDetails}
            className="flex cursor-pointer items-center gap-2"
            aria-label={showDetails ? 'Hide card details' : 'View card details'}
            aria-expanded={showDetails}
            aria-controls="card-details-section">
            {showDetails ? (
              <EyeCloseIcon
                className={`path-fill-current ${isPhysicalCard ? 'text-white' : 'text-primaryText'}`}
                style={{ height: getElementHeight(16), width: getElementHeight(16) }}
              />
            ) : (
              <EyeIcon
                className={`path-fill-current ${isPhysicalCard ? 'text-white' : 'text-primaryText'}`}
                style={{ height: getElementHeight(16), width: getElementHeight(16) }}
              />
            )}
            <span className={`text-px13 font-medium ${isPhysicalCard ? 'text-white' : 'text-primaryText'}`}>
              {showDetails ? 'Hide' : 'Show'}
            </span>
          </div>
        </div>

        <div className="flex justify-start">{renderStatusBanner({ config: statusBannerConfig })}</div>

        <CardDetails
          cardHolderName={cardDataFromApi.cardHolderName}
          cardNumberLast4={cardDataFromApi.cardNumberLast4}
          showDetails={showDetails}
          contentTextColor={contentTextColor}
          cardNumberDotColor={cardNumberDotColor}
          getElementHeight={getElementHeight}
          getElementWidth={getElementWidth}
        />

        {isDetailsLoading && (
          <div
            className="absolute top-1/2 left-1/2 flex h-full w-full 
            -translate-x-1/2 -translate-y-1/2 items-center justify-center"
            role="status"
            aria-live="polite"
            aria-label="Loading card details">
            <div className="h-6 w-6">
              <Loader lottieClassName="opacity-80 h-full w-full" />
            </div>
          </div>
        )}
      </CardBackground>
    </div>
  );
};

export default React.memo(CardView);
